// Presentation model for the slot lifecycle. The backend FSM
// (PLANNED → GENERATING_IDEAS → IDEATION → SIGNED_OFF → IN_CLIENT_REVIEW →
// CLIENT_APPROVED, plus MISSED) reads in the product as a six-stop line:
//
//   Plan → Research → Concept → Client review → Creative brief → Production
//
// There is no Pitch stop: picking one of the three ideas IS the internal
// review. The "Client" stop is where the per-slot regime shows up — each post
// is sent, decided and reworked on its own, so two cards in the same month can
// legitimately sit five stops apart.
//
// This module is the single place that maps a slot onto that line — its
// position, whose turn it is, and the words the UI uses for it. Color follows
// the six-swatch brand system in both themes: Blue = active/in progress,
// Purple = idea/decision work, Green = approved/live, and neutral = queued or
// failed. Labels and icons remain mandatory, so color never carries meaning alone.

import {
  SLOT_CONTENT_FINAL_STATUSES,
  isDirectSlot,
  isSendableSlot,
  openDeclineFor,
  type CalendarPlanSlot,
  type CalendarSlotStatus,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { isPastDayUtc } from "./calendar.utils";
import { publicationIntegrityOf } from "./publicationIntegrity";

/**
 * SIX stops. "Client" is its own stop rather than a mode of sign-off, because
 * per-slot it is a real place a post sits for days while its neighbours move on.
 *
 * The first stop is "Plan", not "Brief": that word now means the production
 * document, and having it label both ends of the same rail was the single most
 * confusing thing in this UI.
 */
export const STAGE_LABELS = [
  "Plan",
  "Research",
  "Concept",
  "Client review",
  "Creative brief",
  "Production",
] as const;

/**
 * A quick post has no pipeline behind it, so it gets its own two-stop reading
 * (media → scheduled) rather than a rail with four inapplicable stops greyed out.
 */
export const DIRECT_STAGE_LABELS = ["Media", "Scheduled"] as const;

/**
 * How much a caller knows about the production brief.
 *
 * A tri-state, encoded as a discriminated union rather than an optional
 * `brief?: BriefRailInfo | null`, because collapsing "I did not ask" into "there
 * is not one" is exactly how this would silently change every existing call site.
 * `{ known: false }` — the default — reproduces the pre-brief behaviour verbatim.
 */
export type BriefAwareness = { known: false } | { known: true; brief: BriefRailInfo | null };

/** The default for every caller that has not been taught about briefs. */
export const BRIEF_UNKNOWN: BriefAwareness = { known: false };

/** Whose turn a seat is waiting on. Drives every color decision in this UI. */
export type SlotTone = "queued" | "machine" | "human" | "ready" | "missed";

/**
 * The last research run failed and nothing will retry it — it needs a person.
 * There is no nightly ideation pass, so one failure is already the end of it.
 */
export function researchStalled(slot: CalendarPlanSlot): boolean {
  return slot.status === "PLANNED" && !!slot.ideaError;
}

// ------------------------------------------------------------- Stuck detection

/**
 * TWO thresholds, because "you may restart this" and "this is broken" are
 * different claims and conflating them makes one of them lie.
 *
 * `RECOVERY_OFFER_AFTER_MS` mirrors FORCE_RECOVER_MIN_MS on the backend: the
 * point where the escape hatch becomes available. It is short on purpose — the
 * operator should never have to wait to be allowed to act — and the server
 * enforces the same floor inside the claim, so a button pressed early is refused
 * there. Keep the two in step.
 *
 * `LOOKS_STUCK_AFTER_MS` is the point where we are willing to SAY something is
 * stuck: amber tone, the word "Stuck", and a move out of the month pulse's
 * "agents working" bucket. It matches the automatic sweep's 45 minutes, i.e. the
 * age at which the backend itself gives up on a run. Anything shorter would paint
 * every healthy post as broken, since a real research run takes 20-30 minutes.
 */
export const RECOVERY_OFFER_AFTER_MS = 5 * 60_000;
export const LOOKS_STUCK_AFTER_MS = 45 * 60_000;

/** Whole minutes an entity has sat in its current state. */
export function minutesInState(updatedAt: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(updatedAt).getTime()) / 60_000));
}

function olderThan(updatedAt: string, now: number, ms: number): boolean {
  return now - new Date(updatedAt).getTime() >= ms;
}

// ---- "may a person act on this?" — drives the buttons ----

/** A researching post the operator may restart. Not a claim that anything is wrong. */
export function canRestartResearch(slot: CalendarPlanSlot, now: number): boolean {
  return slot.status === "GENERATING_IDEAS" && olderThan(slot.updatedAt, now, RECOVERY_OFFER_AFTER_MS);
}

/** An idea mid-rework the operator may cancel. Free and non-destructive, so offered early. */
export function canCancelRework(
  idea: { status: string; updatedAt: string },
  now: number,
): boolean {
  return idea.status === "WORKING" && olderThan(idea.updatedAt, now, RECOVERY_OFFER_AFTER_MS);
}

/** A brief mid-write the operator may condemn. */
export function canFailBrief(
  brief: { status: string; updatedAt: string } | null | undefined,
  now: number,
): boolean {
  return (
    !!brief && brief.status === "GENERATING" && olderThan(brief.updatedAt, now, RECOVERY_OFFER_AFTER_MS)
  );
}

/**
 * A brief the team may accept themselves. Everything reviewable and not already
 * settled: GENERATING and FAILED have nothing to sign off, ACCEPTED is done.
 */
export function canTeamAcceptBrief(brief: { status: string } | null | undefined): boolean {
  return (
    !!brief &&
    (brief.status === "DRAFT" ||
      brief.status === "IN_REVIEW" ||
      brief.status === "CHANGES_REQUESTED")
  );
}

/** A month mid-plan the operator may restart. */
export function canRestartPlanning(
  plan: { status: string; updatedAt: string } | null | undefined,
  now: number,
): boolean {
  return (
    !!plan && plan.status === "GENERATING" && olderThan(plan.updatedAt, now, RECOVERY_OFFER_AFTER_MS)
  );
}

// ---- "is this actually broken?" — drives tone, labels and pulse counts ----

/**
 * A post that has been "Researching" long past any real run.
 *
 * This is the one that turns a card amber and calls it "Stuck", so it holds to
 * the stricter threshold: `updatedAt` is stamped at claim and not during the run,
 * which means a perfectly healthy 25-minute run is indistinguishable from a dead
 * one until the sweep's own deadline passes.
 */
export function isStuckResearching(slot: CalendarPlanSlot, now: number): boolean {
  return slot.status === "GENERATING_IDEAS" && olderThan(slot.updatedAt, now, LOOKS_STUCK_AFTER_MS);
}

/** An idea card frozen mid-rework, long past any real rework. */
export function isStuckRework(idea: { status: string; updatedAt: string }, now: number): boolean {
  return idea.status === "WORKING" && olderThan(idea.updatedAt, now, LOOKS_STUCK_AFTER_MS);
}

/** A production brief frozen mid-write, long past any real write. */
export function isStuckBrief(
  brief: { status: string; updatedAt: string } | null | undefined,
  now: number,
): boolean {
  return (
    !!brief && brief.status === "GENERATING" && olderThan(brief.updatedAt, now, LOOKS_STUCK_AFTER_MS)
  );
}

/** A month frozen mid-plan, long past any real plan run. */
export function isStuckPlanning(
  plan: { status: string; updatedAt: string } | null | undefined,
  now: number,
): boolean {
  return !!plan && plan.status === "GENERATING" && olderThan(plan.updatedAt, now, LOOKS_STUCK_AFTER_MS);
}

/**
 * Is anything in this month in a machine state at all?
 *
 * The gate on the stuck clock: a settled month should not run a timer. Note it
 * counts WORKING ideas and GENERATING briefs as well as researching posts —
 * both hold claims that no slot status reveals.
 */
export function anyMachineWork(
  slots: CalendarPlanSlot[],
  briefBySlot?: Map<string, BriefRailInfo>,
): boolean {
  return slots.some(
    (s) =>
      s.status === "GENERATING_IDEAS" ||
      (s.ideas ?? []).some((i) => i.status === "WORKING") ||
      briefBySlot?.get(s.id)?.status === "GENERATING",
  );
}

/**
 * `now` is optional on every stage function below, and omitting it reproduces the
 * pre-recovery behaviour exactly. Passing it opts a surface into showing stuck
 * work: a post frozen on "Researching" flips from `machine` (agents in motion) to
 * `human` (your call), because that is what it has actually become.
 */
export function slotTone(
  slot: CalendarPlanSlot,
  briefs: BriefAwareness = BRIEF_UNKNOWN,
  now?: number,
): SlotTone {
  // A decline sends a signed-off post back to PLANNED (or, once the redo is
  // moving, GENERATING_IDEAS / IDEATION) — the same statuses a post that was
  // simply never touched yet passes through. Without this check a rejected
  // idea reads identically to one nobody has looked at, which is exactly the
  // gap being closed here. MISSED stays MISSED: the day is gone regardless of
  // what the client said about the idea.
  if (slot.status !== "MISSED" && openDeclineFor(slot)) return "human";
  // A quick post has no ideas and no brief; its state is entirely "is the media
  // there and is it scheduled", which the deliverable answers.
  if (isDirectSlot(slot)) return directTone(slot);
  const publishingTone = slot.status === "MISSED" ? null : publicationTone(slot);
  if (publishingTone) return publishingTone;
  switch (slot.status) {
    case "PLANNED":
      return researchStalled(slot) ? "human" : "queued";
    case "GENERATING_IDEAS":
      return now !== undefined && isStuckResearching(slot, now) ? "human" : "machine";
    case "IDEATION":
      return "human";
    case "SIGNED_OFF":
      return "human";
    case "IN_CLIENT_REVIEW":
      // Waiting on someone outside the building; nothing the team does moves it.
      return "queued";
    case "CLIENT_APPROVED":
      return signedOffTone(briefs);
    case "MISSED":
      return "missed";
  }
}

/**
 * Publication is a per-target contract. A successful target only makes the
 * whole post ready when every other current target is resolved or safely queued.
 */
function publicationTone(slot: CalendarPlanSlot): SlotTone | null {
  const deliverable = slot.deliverable ?? null;
  if (!deliverable) return null;
  if (deliverable.status === "FAILED") return "human";
  if (deliverable.status !== "READY") return "machine";
  const publication = publicationIntegrityOf(slot);
  switch (publication.state) {
    case "live":
      return "ready";
    case "retry_scheduled":
      return "queued";
    case "publishing":
      return "machine";
    case "partially_scheduled":
      return "human";
    case "scheduled":
      return publication.needsAction ? "human" : "ready";
    case "partially_live":
      return publication.needsAction ? "human" : "ready";
    case "failed":
    case "manual_required":
    case "setup_required":
    case "ready":
    case null:
      return "human";
  }
}

/** A quick post has no earlier pipeline beyond media and publishing. */
function directTone(slot: CalendarPlanSlot): SlotTone {
  return publicationTone(slot) ?? "human";
}

/**
 * A signed-off post reads "ready" to a brief-unaware caller, exactly as it did
 * before this stage existed. To a brief-aware one it reads honestly — a month
 * whose briefs have all been declined is not twelve-of-twelve green.
 */
function signedOffTone(briefs: BriefAwareness): SlotTone {
  if (!briefs.known) return "ready";
  if (!briefs.brief) return "queued";
  switch (briefs.brief.status) {
    case "GENERATING":
      return "machine";
    case "DRAFT":
    case "CHANGES_REQUESTED":
    case "FAILED":
      return "human";
    case "IN_REVIEW":
      return "queued";
    case "ACCEPTED":
      return "ready";
    default:
      return "ready";
  }
}

/**
 * Where a drag leaves a post's status — mirrors `statusAfterMove` on the backend
 * so the optimistic card doesn't flash the wrong tone before the plan refetches.
 *
 * Only the two statuses that are DEFINED by which side of today the post sits on
 * follow the move: a post dropped on a day that has passed is missed, and a
 * missed post dragged forward is live work again. Everything from sign-off
 * onward keeps its status wherever it lands.
 */
export function statusAfterMove(
  status: CalendarSlotStatus,
  target: { year: number; month: number; day: number },
): CalendarSlotStatus {
  if (isPastDayUtc(target.year, target.month, target.day)) {
    return (SLOT_CONTENT_FINAL_STATUSES as string[]).includes(status) || status === "MISSED"
      ? status
      : "MISSED";
  }
  return status === "MISSED" ? "PLANNED" : status;
}

/** May this post be dragged at all? A post under a client's eyes may not. */
export function canMoveSlot(slot: CalendarPlanSlot): boolean {
  return slot.status !== "IN_CLIENT_REVIEW";
}

/**
 * How the production rail renders for a slot. A single "line" shape covers every
 * state: `done` stops are complete, `active` (nullable) is the in-progress stop,
 * and `readyDone` colors the completed run green (signed off) rather than lime.
 */
export type RailState =
  | { kind: "missed" }
  | {
      kind: "line";
      /** Stops fully completed (0-4). */
      done: number;
      /** The stop in progress / waiting, or null when nothing is pending. */
      active: number | null;
      activeTone: "machine" | "human" | "queued" | null;
      /** Render completed stops green (signed off) instead of lime. */
      readyDone: boolean;
    };

export function railState(
  slot: CalendarPlanSlot,
  briefs: BriefAwareness = BRIEF_UNKNOWN,
  now?: number,
): RailState {
  // A quick post never entered the pipeline, so it has no rail position to
  // report. Callers that know about DIRECT posts render the two-stop reading
  // instead; the rest fall back to "all done", which is honest for a post that
  // was made outside the process.
  if (isDirectSlot(slot)) {
    return {
      kind: "line",
      done: STAGE_LABELS.length,
      active: null,
      activeTone: null,
      readyDone: true,
    };
  }
  switch (slot.status) {
    case "PLANNED":
      return {
        kind: "line",
        done: 1,
        active: 1,
        activeTone: researchStalled(slot) ? "human" : "queued",
        readyDone: false,
      };
    case "GENERATING_IDEAS":
      return {
        kind: "line",
        done: 1,
        active: 1,
        activeTone: now !== undefined && isStuckResearching(slot, now) ? "human" : "machine",
        readyDone: false,
      };
    case "IDEATION":
      return { kind: "line", done: 2, active: 2, activeTone: "human", readyDone: false };
    case "SIGNED_OFF":
      return { kind: "line", done: 3, active: 3, activeTone: "human", readyDone: false };
    case "IN_CLIENT_REVIEW":
      return { kind: "line", done: 3, active: 3, activeTone: "queued", readyDone: false };
    case "CLIENT_APPROVED": {
      if (briefs.known && briefs.brief?.status !== "ACCEPTED") {
        return {
          kind: "line",
          done: 4,
          active: 4,
          activeTone:
            briefs.brief?.status === "GENERATING" || !briefs.brief
              ? "machine"
              : briefs.brief?.status === "IN_REVIEW"
                ? "queued"
                : "human",
          readyDone: false,
        };
      }
      return {
        kind: "line",
        done: 5,
        active: 5,
        activeTone: "human",
        readyDone: true,
      };
    }
    case "MISSED":
      return { kind: "missed" };
  }
}

/**
 * The two-stop reading for a quick post: has it got media, and is it out the
 * door. Returned separately from `railState` because the vocabularies genuinely
 * differ — a DIRECT post has no research, ideation or client stop to be at.
 */
export type DirectState = {
  /** 0 = no media yet, 1 = media ready, 2 = scheduled or published. */
  done: number;
  label: string;
  tone: SlotTone;
};

export function directState(slot: CalendarPlanSlot): DirectState {
  const deliverable = slot.deliverable ?? null;
  const tone = directTone(slot);
  if (!deliverable) return { done: 0, label: "Add media", tone };
  if (deliverable.status === "FAILED") return { done: 0, label: "Upload failed", tone };
  if (deliverable.status !== "READY") return { done: 0, label: "Processing…", tone };

  const publication = publicationIntegrityOf(slot);
  switch (publication.state) {
    case "live":
      return { done: 2, label: "Live", tone };
    case "retry_scheduled":
      return { done: 2, label: "Retry scheduled", tone };
    case "publishing":
      return { done: 1, label: "Publishing…", tone };
    case "partially_scheduled":
      return { done: 1, label: "Finish scheduling", tone };
    case "scheduled":
      return publication.needsAction
        ? { done: 1, label: "Finish scheduling", tone }
        : { done: 2, label: "Scheduled", tone };
    case "partially_live":
      return { done: 1, label: "Partially live", tone };
    case "failed":
      return { done: 1, label: "Failed", tone };
    case "manual_required":
      return { done: 1, label: "Post manually", tone };
    case "setup_required":
      return { done: 1, label: "Publishing setup", tone };
    case "ready":
      return {
        done: 1,
        label: publication.intendedChannels.length === 0 ? "Pick platforms" : "Ready to schedule",
        tone,
      };
    case null:
      return { done: 1, label: "Not scheduled", tone };
  }
}

/**
 * Short state word for chips and rows.
 *
 * `briefs` defaults to "don't know" — a caller that hasn't been taught about
 * the brief stage gets the idea-level words back. `planStatus` is kept in the
 * signature for call-site compatibility but is no longer consulted: the post's
 * own status answers where it is now.
 */
export function statusLabel(
  slot: CalendarPlanSlot,
  _planStatus?: string,
  briefs: BriefAwareness = BRIEF_UNKNOWN,
  now?: number,
): string {
  if (isDirectSlot(slot)) return directState(slot).label;
  if (slot.status !== "MISSED" && openDeclineFor(slot)) return "Feedback to align";
  if (now !== undefined && isStuckResearching(slot, now)) return "Stuck";

  if (slot.status === "CLIENT_APPROVED" && briefs.known) {
    if (!briefs.brief) return "Writing brief…";
    switch (briefs.brief.status) {
      case "GENERATING":
        return "Writing brief…";
      case "DRAFT":
        return "Creative brief ready";
      case "CHANGES_REQUESTED":
        return "Changes requested";
      case "FAILED":
        return "Brief needs writing";
    }
  }

  if (slot.status === "CLIENT_APPROVED") {
    if (briefs.known) {
      if (!briefs.brief) return "Awaiting brief";
      switch (briefs.brief.status) {
        case "GENERATING":
          return "Briefing…";
        case "IN_REVIEW":
          return "Brief with client";
        case "ACCEPTED":
          return "Brief accepted";
        // DRAFT / CHANGES_REQUESTED / FAILED are tone "human" — humanCallLabel
        // owns those, so a caller branching on tone first never reaches here
        // for them. Falling through hands back the words below, which stay
        // correct even if that branching ever changes.
      }
    }
    // The stop is complete either way — this only names who closed it.
    return slot.approvalSource === "TEAM" ? "Approved by team" : "Client approved";
  }

  switch (slot.status) {
    case "PLANNED":
      return researchStalled(slot) ? "Stalled" : "Not started";
    case "GENERATING_IDEAS":
      return "Researching";
    case "IDEATION":
      return "Pick an idea";
    case "SIGNED_OFF":
      return "Ready to share";
    case "IN_CLIENT_REVIEW":
      return "With client";
    case "MISSED":
      return "Missed";
  }
}

/**
 * The word a "your call" card (tone === "human") shows — what specifically
 * needs a person. Shared by the list row and the month grid card so the two
 * surfaces can't drift the way `researchStalled ? "Stalled" : "Your call"`
 * already had before this was pulled out from under both of them.
 *
 * `briefs` mirrors the param on `slotTone`: omit it and a SIGNED_OFF post never
 * reads as anything but the idea-level checks below — a caller has to opt in
 * before a brief's own state (rejected / failed / unsent) shows through.
 */
export function humanCallLabel(
  slot: CalendarPlanSlot,
  briefs: BriefAwareness = BRIEF_UNKNOWN,
  now?: number,
): string {
  if (isDirectSlot(slot)) return directState(slot).label;
  if (now !== undefined && isStuckResearching(slot, now)) return "Stuck — restart?";
  if (slot.status === "CLIENT_APPROVED" && briefs.known && briefs.brief) {
    switch (briefs.brief.status) {
      case "CHANGES_REQUESTED":
        return "Changes requested";
      case "FAILED":
        return "Brief failed";
      case "DRAFT":
        return "Creative brief ready";
    }
  }
  if (openDeclineFor(slot)) return "Review feedback";
  if (slot.status === "SIGNED_OFF") return "Share with client";
  return researchStalled(slot) ? "Stalled" : "Your call";
}

/** The idea a slot was signed off on, if any. */
export function acceptedIdea(slot: CalendarPlanSlot) {
  return (slot.ideas ?? []).find((i) => i.status === "ACCEPTED") ?? null;
}

/** Display title: the signed-off idea's title once one exists, else the brief. */
export function slotTitle(slot: CalendarPlanSlot): string {
  if (slot.status === "SIGNED_OFF") return acceptedIdea(slot)?.title || slot.title;
  return slot.title;
}

export function isGenerating(status: CalendarSlotStatus): boolean {
  return status === "GENERATING_IDEAS";
}

/** Delete is held while agents are writing to the post or it is posting right now. */
export function slotDeleteBlocked(slot: CalendarPlanSlot): boolean {
  return (
    isGenerating(slot.status) ||
    (slot.deliverable?.publications ?? []).some((p) => p.status === "PUBLISHING")
  );
}

/** One line of supporting text for list rows; null when there is nothing real. */
export function slotPreview(slot: CalendarPlanSlot): string | null {
  if (slot.status === "SIGNED_OFF") return acceptedIdea(slot)?.synopsis ?? null;
  return null;
}

export interface MonthPulseCounts {
  total: number;
  ready: number;
  /** IDEATION + stalled PLANNED seats — decisions waiting on a person. */
  human: number;
  machine: number;
  queued: number;
  missed: number;
}

export function monthPulseCounts(
  slots: CalendarPlanSlot[],
  briefBySlot?: Map<string, BriefRailInfo>,
  now?: number,
): MonthPulseCounts {
  const counts: MonthPulseCounts = {
    total: slots.length,
    ready: 0,
    human: 0,
    machine: 0,
    queued: 0,
    missed: 0,
  };
  for (const slot of slots) {
    const awareness: BriefAwareness = briefBySlot
      ? { known: true, brief: briefBySlot.get(slot.id) ?? null }
      : BRIEF_UNKNOWN;
    counts[slotTone(slot, awareness, now)]++;
  }
  return counts;
}

// ---------------------------------------------------------------- Plan stage

/**
 * Whose turn the MONTH is waiting on — the banner above the grid keys off this.
 * `client` is its own tone because it is the one state where the month is
 * waiting on somebody outside the building: nothing the team does moves it.
 */
export type PlanTone = "empty" | "machine" | "human" | "live" | "client" | "done" | "archived";

export function planTone(status: CalendarPlanStatusLike): PlanTone {
  switch (status) {
    case "DRAFT":
      return "empty";
    case "GENERATING":
      return "machine";
    case "APPROVED":
      return "live";
    case "IN_CLIENT_REVIEW":
    case "CHANGES_REQUESTED":
      return "live";
    // Every legacy status reads as the live month it now is.
    case "PENDING_APPROVAL":
    case "CLIENT_APPROVED":
    case "BRIEFS_GENERATING":
    case "BRIEFS_DRAFT":
    case "BRIEFS_CHANGES_REQUESTED":
    case "BRIEFS_IN_REVIEW":
    case "MONTH_SIGNED_OFF":
      return "live";
    case "ARCHIVED":
      return "archived";
    default:
      return "empty";
  }
}

type CalendarPlanStatusLike = string;

export function planStatusLabel(status: CalendarPlanStatusLike): string {
  switch (status) {
    case "DRAFT":
      return "Not planned";
    case "GENERATING":
      return "Planning";
    // APPROVED is where a working month lives for the rest of its life; every
    // legacy status means the same thing on an older row.
    case "APPROVED":
    case "PENDING_APPROVAL":
    case "IN_CLIENT_REVIEW":
    case "CHANGES_REQUESTED":
    case "CLIENT_APPROVED":
    case "BRIEFS_GENERATING":
    case "BRIEFS_DRAFT":
    case "BRIEFS_IN_REVIEW":
    case "BRIEFS_CHANGES_REQUESTED":
    case "MONTH_SIGNED_OFF":
      return "In production";
    case "ARCHIVED":
      return "Archived";
    default:
      return status;
  }
}

/**
 * The month hero badge.
 *
 * Up to the end of the plan run this is still a ladder — the month really does
 * move as one unit while it is being planned. After it, there is no single stage
 * to name, because the posts are all in different places at once. So the badge
 * switches from "where the month is" to "what it is carrying": the headline
 * number of posts needing a person, with the plain "In production" when nothing
 * does.
 */
export function planStageMeta(
  status: CalendarPlanStatusLike,
  slots: CalendarPlanSlot[] = [],
): { stage: number; label: string } {
  switch (status) {
    case "GENERATING":
      return { stage: 1, label: "Planning" };
    case "ARCHIVED":
      return { stage: 4, label: "Archived" };
    case "DRAFT":
    case "NONE":
      return { stage: 1, label: "Not planned" };
    default:
      break;
  }

  // APPROVED and every legacy status land here: one live stage with the posts
  // doing the moving.
  const needsYou = slots.filter(
    (s) => s.status === "IDEATION" || (s.status === "PLANNED" && researchStalled(s)),
  ).length;
  const ready = sendableSlots(slots).length;
  const outWithClient = slots.filter((s) => s.status === "IN_CLIENT_REVIEW").length;

  if (ready > 0) {
    return { stage: 3, label: `${ready} ready to send` };
  }
  if (needsYou > 0) {
    return { stage: 3, label: `${needsYou} need${needsYou === 1 ? "s" : ""} you` };
  }
  if (outWithClient > 0) {
    return { stage: 3, label: `${outWithClient} with client` };
  }
  return { stage: 3, label: "In production" };
}

/**
 * The posts that could be sent to the client right now. Mirrors the backend's
 * eligibility rule exactly (pipeline, signed off, has an accepted idea), so the
 * button's count is the number of posts the call would actually send.
 *
 * Nothing "blocks" a send anymore — a month with eleven unfinished posts and
 * one ready one sends that one. `slotsBlockingSend` is gone with the idea that
 * the month has to be complete.
 */
export function sendableSlots(slots: CalendarPlanSlot[]): CalendarPlanSlot[] {
  return slots.filter((s) => isSendableSlot(s));
}

/** Posts currently sitting in front of a client, waiting for an answer. */
export function slotsWithClient(slots: CalendarPlanSlot[]): CalendarPlanSlot[] {
  return slots.filter((s) => s.status === "IN_CLIENT_REVIEW");
}

/**
 * Briefs the operator could send right now. The brief-stage twin of
 * `sendableSlots`, and the count the "Send N briefs" button shows.
 */
export function sendableBriefSlots(
  slots: CalendarPlanSlot[],
  briefBySlot: Map<string, BriefRailInfo>,
): CalendarPlanSlot[] {
  return slots.filter((s) => {
    const brief = briefBySlot.get(s.id);
    return brief?.status === "DRAFT" || brief?.status === "CHANGES_REQUESTED";
  });
}

/** Briefs sitting in front of a client right now. */
export function briefsWithClient(
  slots: CalendarPlanSlot[],
  briefBySlot: Map<string, BriefRailInfo>,
): CalendarPlanSlot[] {
  return slots.filter((s) => briefBySlot.get(s.id)?.status === "IN_REVIEW");
}
